import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"
import styled from "styled-components"

import {
    CcRequirementState,
    CcRequirementHistory,
    CcRequirementHistoryImportProps,
    CcRequirementRevision,
    CcRequirementTempAnswer,
    CcRequirementTempAnswerVersion,
} from "server/models"
import {
    IDLE, PENDING, EDIT,
    WORKFLOW_RERUN,
    DESC,
} from "const"
import { StateType, CcStateSettingsType, hasProjectAccess, deepCopy } from "client/utils"
import { sort } from "client/store/utils"
import {
    PRM_CC_REQUIREMENT_REVIEW,
    StoreType,
    CcRequirementObjectProps,
    RequirementList,
    RequirementRevisionValidation,

    LoadCcRequirementRevisions,
    LoadCcRequirementState,
    LoadCcRequirementHistory,
    LoadCcRequirementRevisionData,

    CreateCcRequirementState,
    ImportCcRequirementHistory,
    CreateCcRequirementRevision,
    UpdateCcRequirementRevision,
    SaveCcRequirementTempAnswer,
    SaveCcRequirementTempAnswerVersions,
    DeleteCcRequirementRevision,
    DeleteCcRequirement,

    setCcRequirementRevisionList,
    setCcRequirementRevisionActive,
    setCcRequirementIds,
    setCcRequirementList,
    setCcRequirementReviewListLoadState,

    initCcRequirementHistory,
} from "client/store"
import {
    SelectDropdown,
    SelectDropdownItem,
    FormGroup,
    Label,
    Input,
    Feedback,
    Button,
    SpinnerIcon,

    ConfirmWrapper,
    Confirm,
    ConfirmTitle,
    ConfirmDescription,
    ConfirmBottom,

    PrepareNestedHistory,
} from "client/components"


const RequirementRevisionWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;

    & > ${Button} {
        margin-bottom: 0;
    }
`


const RequirementRevisionList = ({
    requirement,
}: CcRequirementObjectProps) => {
    const requirementList = useSelector((state: StoreType) => state.ccRequirement.requirementList)
    const requirementIds = useSelector((state: StoreType) => state.ccRequirement.requirementIds)
    const revisionList = useSelector((state: StoreType) => state.ccRequirement.revisionList)
    const revisionActiveList = useSelector((state: StoreType) => state.ccRequirement.revisionActiveList)

    const moduleLevelList = useSelector((state: StoreType) => state.ccApprovalLevel.moduleLevelList)
    const levelIds = moduleLevelList[3] || []

    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [revisions, setRevisions] = useState<CcRequirementRevision[]>([])

    const [showDelete, setShowDelete] = useState(false)
    const [showRename, setShowRename] = useState(false)

    const [revisionName, setRevisionName] = useState("")
    const [revisionValidation, setRevisionValidation] = useState<RequirementRevisionValidation>(null)
    const [renameState, setRenameState] = useState<StateType>(IDLE)

    const activeRevision = revisionActiveList[requirement.id] || null
    const editAccess = hasProjectAccess(PRM_CC_REQUIREMENT_REVIEW, EDIT)

    useEffect(() => {
        // display the last revision on top
        const revisionArray: CcRequirementRevision[] = revisionList[requirement.id] ? revisionList[requirement.id].slice() : []
        revisionArray.sort((a, b) => sort(a, b, "number", DESC))
        setRevisions(revisionArray)
    }, [revisionList])

    const changeActiveRevision = (revision: CcRequirementRevision) => {
        dispatch(setCcRequirementReviewListLoadState(PENDING))
        dispatch(setCcRequirementRevisionActive(requirement.id, revision))

        LoadCcRequirementRevisionData(requirement.id, revision.id, dispatch).then(() => {
            dispatch(setCcRequirementReviewListLoadState(IDLE))
        })
    }

    const onCancelDelete = () => {
        setShowDelete(false)
    }
    const onConfirmDelete = () => {
        // Delete revision. If this is the only revision - remove requirement
        setShowDelete(false)
        dispatch(setCcRequirementReviewListLoadState(PENDING))

        DeleteCcRequirementRevision(activeRevision.id).then(() => {
            LoadCcRequirementRevisions(requirement.id).then((newRevisions) => {
                if (newRevisions.length > 0) {
                    // set new revision list and load revision data
                    const lastRevision = newRevisions[newRevisions.length - 1]
                    dispatch(setCcRequirementRevisionList(requirement.id, newRevisions))
                    dispatch(setCcRequirementRevisionActive(requirement.id, lastRevision))

                    LoadCcRequirementRevisionData(requirement.id, lastRevision.id, dispatch).then(() => {
                        dispatch(setCcRequirementReviewListLoadState(IDLE))
                    })
                } else {
                    // remove item
                    DeleteCcRequirement(requirement.id, requirement.projectId).then(() => {
                        const newRequirementIds = requirementIds.filter((requirementId) => requirementId !== requirement.id)
                        const newRequirementList: RequirementList = deepCopy(requirementList)
                        delete newRequirementList[requirement.id]

                        dispatch(setCcRequirementIds(newRequirementIds))
                        dispatch(setCcRequirementList(newRequirementList))
                        dispatch(setCcRequirementReviewListLoadState(IDLE))
                    })
                }
            })
        })
    }

    const onCancelRename = () => {
        setShowRename(false)
    }
    const onConfirmRename = () => {
        setRenameState(PENDING)
        UpdateCcRequirementRevision(activeRevision.id, revisionName, lang).then((validation) => {
            if (validation) {
                setRevisionValidation(validation)
                setRenameState(IDLE)
            } else {
                LoadCcRequirementRevisions(requirement.id).then((newRevisions) => {
                    const newActiveRevision = newRevisions.find((row) => row.id === activeRevision.id)
                    dispatch(setCcRequirementRevisionList(requirement.id, newRevisions))
                    dispatch(setCcRequirementRevisionActive(requirement.id, newActiveRevision))

                    setRenameState(IDLE)
                    setShowRename(false)
                })
            }
        })
    }

    const onRerun = () => {
        dispatch(setCcRequirementReviewListLoadState(PENDING))

        LoadCcRequirementRevisions(requirement.id).then((resRevisions) => {
            CreateCcRequirementRevision(requirement.id)
            .then((newRevision) => {
                const lastRevision = resRevisions[resRevisions.length - 1]

                Promise.all([
                    LoadCcRequirementState(requirement.id, lastRevision.id),
                    LoadCcRequirementHistory(requirement.id, lastRevision.id),
                ]).then(([requirementState, requirementHistory]) => {
                    const promiseArray: Promise<any>[] = []
                    const settings: CcStateSettingsType = requirementState && requirementState.settings
                        ? JSON.parse(requirementState.settings)
                        : {}

                    let lastHistory: CcRequirementHistory = null
                    let lastResponsibleAnswer: CcRequirementHistory = null
                    for (let i = requirementHistory.length - 1; i >= 0; i--) {
                        const history = requirementHistory[i]
                        if (history.workflow === WORKFLOW_RERUN) {
                            break
                        }
                        if (!history.systemMessage) {
                            if (!lastHistory) lastHistory = history
                            if (!lastResponsibleAnswer && history.parentId === null) lastResponsibleAnswer = history
                        }
                        if (lastHistory && lastResponsibleAnswer) break
                    }

                    const newHistory: CcRequirementHistory = {
                        ...initCcRequirementHistory,
                        id: null,
                        requirementId: requirement.id,
                        requirementRevisionId: newRevision.id,
                        parentId: null,
                        workflow: WORKFLOW_RERUN,
                        systemMessage: true,
                        editorId: userData.userId,
                    }
                    const newState: CcRequirementState = {
                        ...requirementState,
                        requirementRevisionId: newRevision.id,
                        levelId: levelIds[0] || null,
                        verifierId: (levelIds[0] && settings[levelIds[0]]) || null,
                    }

                    requirementHistory.push(newHistory)

                    if (lastResponsibleAnswer) {
                        const tempAnswer: CcRequirementTempAnswer = {
                            requirementId: requirement.id,
                            requirementRevisionId: newRevision.id,
                            editorId: lastResponsibleAnswer.prevUserId,
                            responseId: lastResponsibleAnswer.responseId,
                            remark: lastResponsibleAnswer.remark,
                            nextUserId: lastResponsibleAnswer.nextUserId,
                        }

                        const tempVersions: CcRequirementTempAnswerVersion[] = []
                        lastResponsibleAnswer.versions.forEach((version) => {
                            tempVersions.push({
                                requirementId: requirement.id,
                                requirementRevisionId: newRevision.id,
                                editorId: lastResponsibleAnswer.prevUserId,
                                versionId: version.versionId,
                            })
                        })

                        promiseArray.push(
                            SaveCcRequirementTempAnswer(tempAnswer, lang).then(() => (
                                SaveCcRequirementTempAnswerVersions(tempVersions)
                            )),
                        )
                    }

                    const nestedHistory = PrepareNestedHistory(requirementHistory)

                    const importData: CcRequirementHistoryImportProps = {
                        nestedHistory,
                        newRevisionId: newRevision.id,
                    }
                    promiseArray.push(
                        ImportCcRequirementHistory(importData),
                        CreateCcRequirementState(newState),
                    )

                    Promise.all(promiseArray).then(() => {
                        // reload data
                        LoadCcRequirementRevisions(requirement.id)
                        .then((newRevisions) => {
                            const newLastRevision = newRevisions[newRevisions.length - 1]
                            dispatch(setCcRequirementRevisionList(requirement.id, newRevisions))
                            dispatch(setCcRequirementRevisionActive(requirement.id, newLastRevision))

                            LoadCcRequirementRevisionData(requirement.id, newLastRevision.id, dispatch).then(() => {
                                dispatch(setCcRequirementReviewListLoadState(IDLE))
                            })
                        })
                    })
                })
            })
        })
    }


    return (
        <RequirementRevisionWrapper onClick={(e) => e.stopPropagation()}>
            <SelectDropdown
                title={activeRevision ? activeRevision.name : ""}
                row="true"
                style={{ width: "120px" }}
            >
                {
                    revisions.map((revision) => (
                        <SelectDropdownItem
                            key={revision.id}
                            onClick={() => changeActiveRevision(revision)}
                        >
                            {revision.name}
                        </SelectDropdownItem>
                    ))
                }
            </SelectDropdown>
            {
                editAccess && (
                    <>
                        <Button
                            row
                            onClick={() => setShowDelete(true)}
                        >
                            {lang.delete}
                        </Button>
                        <Button
                            row
                            variant="success"
                            onClick={() => {
                                setRevisionName(activeRevision.name)
                                setShowRename(true)
                            }}
                        >
                            {lang.rename}
                        </Button>
                        <Button
                            row
                            variant="success"
                            onClick={onRerun}
                        >
                            {lang.rerun}
                        </Button>
                    </>
                )
            }

            <ConfirmWrapper
                show={showDelete}
                onClose={onCancelDelete}
            >
                <Confirm>
                    <ConfirmTitle>{activeRevision && activeRevision.name}</ConfirmTitle>
                    <ConfirmDescription>{lang.confirmDeleteRevision}</ConfirmDescription>
                    <ConfirmBottom>
                        <Button row onClick={onCancelDelete}>{lang.cancel}</Button>
                        <Button variant="success" row onClick={onConfirmDelete}>{lang.confirm}</Button>
                    </ConfirmBottom>
                </Confirm>
            </ConfirmWrapper>

            <ConfirmWrapper
                show={showRename}
                onClose={onCancelRename}
            >
                <Confirm>
                    <ConfirmTitle>{lang.renameRevision}</ConfirmTitle>
                    <Row>
                        <Col xs={12}>
                            <FormGroup>
                                <Label>{lang.name}</Label>
                                <Input type="text" value={revisionName} onChange={(e) => setRevisionName(e.target.value)} />
                                <Feedback>{revisionValidation && revisionValidation.name}</Feedback>
                            </FormGroup>
                        </Col>
                    </Row>

                    <ConfirmBottom>
                        <Button row onClick={onCancelRename}>{lang.cancel}</Button>
                        <Button variant="success" row onClick={onConfirmRename}>
                            {renameState === PENDING ? <SpinnerIcon /> : lang.confirm}
                        </Button>
                    </ConfirmBottom>
                </Confirm>
            </ConfirmWrapper>
        </RequirementRevisionWrapper>
    )
}
export default RequirementRevisionList
